/**
 * @author      sammy.dowds@zumper.com (Sammy Dowds)
 * @copyright   Copyright (c) 2023, Zumper
 */

import type { ButtonProps } from '@chakra-ui/react'
import { ExternalLink } from '@zcode-ui/icons'
import { a11yLinkHandler } from '@zcode/a11y-utils'
import type { AnalyticsC, Origin } from '@zcode/nr-utils'
import type { Listable, Pin, RawPinInventorySearch } from '@zcode/state-type'
import cn from 'classnames'
import { Fragment, type KeyboardEvent, type MouseEvent, useCallback } from 'react'
import { ListingCardCallButton, type OnPaidPhoneClick } from './ListingCardCallButton'
import css from './ListingCardContactSection.module.scss'
import buttonCss from './buttons.module.scss'

export type ListingCardMessageButtonProps = React.RefAttributes<
  HTMLAnchorElement & HTMLButtonElement
> &
  Pick<ButtonProps, 'flexGrow' | 'fontSize' | 'size'> & {
    analyticChannel:
      | typeof AnalyticsC.FULL_DETAIL
      | typeof AnalyticsC.LIST
      | typeof AnalyticsC.LIST_ITEM
      | typeof AnalyticsC.LIST_ITEM_MESSAGED
      | typeof AnalyticsC.PERMANENT_ADDRESS
      | typeof AnalyticsC.PROGRAMMATIC_RENT
      | typeof AnalyticsC.HOMEPAGE
      | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
    dataTestId?: string
    entity: Listable | Pin<RawPinInventorySearch>
    extraPayload?: { currentTab?: string }
    origin: Origin
    isSpotlightVariation?: boolean
  }

export interface ListingCardTourButtonProps extends Pick<ButtonProps, 'flexGrow' | 'variant'> {
  analyticChannel:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
  dataTestId?: string
  entity: Listable | Pin<RawPinInventorySearch>
  extraPayload?: { currentTab?: string }
  isSpotlightVariation?: boolean
  origin: Origin
  size?: string
}

export interface ListingCardContactSectionProps {
  /**
   * This analyticChannel eventually gets passed to MessageButtonContainer,
   * which has code that checks for just a few potential values. We can't just
   * make up a new, random string value to pass here (like "map") -- we would
   * also need to update all of the downstream components so that they can
   * handle that new value appropriately
   */
  analyticChannel:
    | typeof AnalyticsC.FULL_DETAIL
    | typeof AnalyticsC.LIST
    | typeof AnalyticsC.LIST_ITEM
    | typeof AnalyticsC.LIST_ITEM_MESSAGED
    | typeof AnalyticsC.PERMANENT_ADDRESS
    | typeof AnalyticsC.PROGRAMMATIC_RENT
    | typeof AnalyticsC.HOMEPAGE
    | typeof AnalyticsC.AFFORDABILITY_CALCULATOR
  currentTab?: string
  entity: Listable | Pin<RawPinInventorySearch>
  forceDesktop?: boolean
  forceMobile?: boolean
  isLargeCard: boolean
  isLargeSpotlight?: boolean
  isShortTerm: boolean
  isSpotlightVariation?: boolean
  ListingCardMessageButton: React.ComponentType<ListingCardMessageButtonProps>
  ListingCardTourButton: React.ComponentType<ListingCardTourButtonProps>
  onDetailClick?: (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => void
  onPaidPhoneClick: OnPaidPhoneClick
  onShortTermClick?: (e: MouseEvent<HTMLButtonElement>) => void
  origin: Origin
  originTour?: Origin
  paidPhoneMixpanelOrigin?: Parameters<OnPaidPhoneClick>[0]['mixpanelOrigin']
  shouldLimitLeads: boolean
  showBorder: boolean
}

export const ListingCardContactSection = ({
  analyticChannel,
  currentTab,
  entity,
  forceDesktop,
  forceMobile,
  isLargeCard,
  isLargeSpotlight,
  isShortTerm,
  isSpotlightVariation,
  ListingCardMessageButton,
  ListingCardTourButton,
  onDetailClick,
  onPaidPhoneClick,
  onShortTermClick,
  origin,
  originTour = origin,
  paidPhoneMixpanelOrigin,
  shouldLimitLeads,
  showBorder,
}: ListingCardContactSectionProps) => {
  const {
    isExternal,
    isMessageable,
    isShortTermAvailability,
    isShortTermViewV1: shouldOpenThirdParty,
    paidPhoneHref,
  } = entity
  const showMessageButton =
    (isMessageable || isExternal) && !isShortTermAvailability && !shouldLimitLeads
  const handleDetailClick = useCallback(
    (e: KeyboardEvent<HTMLButtonElement> | MouseEvent<HTMLButtonElement>) => {
      if (onDetailClick) {
        e.stopPropagation()
        onDetailClick(e)
      }
    },
    [onDetailClick]
  )

  return (
    <Fragment>
      {/* DESKTOP */}
      <div
        className={cn(
          'ListingCardContactSection-desktop-flexbox',
          css.desktopContactSection,
          isSpotlightVariation && css.desktopContactSectionSpotlight,
          isLargeCard && css.desktopContactSectionLargeCard,
          (showBorder || isLargeCard) && css.desktopContactSectionBorder,
          forceMobile && css.desktopContactSectionMobile,
          forceDesktop && css.desktopContactSectionDesktop,
          isLargeSpotlight && css.desktopContactSectionSpotlightLarge
        )}
      >
        {showMessageButton && !isShortTerm && (
          <Fragment>
            <ListingCardTourButton
              analyticChannel={analyticChannel}
              entity={entity}
              extraPayload={{ currentTab }}
              isSpotlightVariation={isSpotlightVariation}
              origin={originTour}
              size="xs"
            />
            <ListingCardMessageButton
              analyticChannel={analyticChannel}
              entity={entity}
              extraPayload={{ currentTab }}
              isSpotlightVariation={isSpotlightVariation}
              origin={origin}
              size="xs"
            />
          </Fragment>
        )}
        {!showMessageButton && !isShortTerm && (
          <button
            className={cn(
              css.seeDetailsBtn,
              buttonCss.xsButton,
              isSpotlightVariation ? buttonCss.whiteButton : buttonCss.solidButton
            )}
            onClick={handleDetailClick}
            onKeyPress={a11yLinkHandler(handleDetailClick)}
            type="button"
          >
            See details
          </button>
        )}
        {isShortTerm && (
          <button
            className={cn(buttonCss.xsButton, buttonCss.solidButton, css.shortTermCta)}
            type="button"
          >
            {shouldOpenThirdParty && (
              <div className={css.externalLinkContainer}>
                <ExternalLink fill="#fff" />
              </div>
            )}
            <p className={css.shortTermCtaText}>
              {shouldOpenThirdParty ? 'Check availability' : 'See details'}
            </p>
          </button>
        )}
      </div>
      {/* MOBILE */}
      <div
        className={cn(
          css.mobileContactSection,
          isLargeCard && !forceDesktop && css.mobileContactSectionLargeCard,
          isLargeCard && forceMobile && css.mobileContactSectionLargeCardMobile
        )}
      >
        {showMessageButton && !isShortTerm && (
          <Fragment>
            {paidPhoneHref ? (
              <ListingCardCallButton
                entity={entity}
                onPaidPhoneClick={onPaidPhoneClick}
                origin={paidPhoneMixpanelOrigin ?? origin}
                paidPhoneHref={paidPhoneHref}
              />
            ) : (
              <ListingCardTourButton
                analyticChannel={analyticChannel}
                entity={entity}
                extraPayload={{ currentTab }}
                isSpotlightVariation={isSpotlightVariation}
                dataTestId="listing-card-tour-mobile"
                origin={originTour}
                size="md"
              />
            )}
            <ListingCardMessageButton
              analyticChannel={analyticChannel}
              entity={entity}
              extraPayload={{ currentTab }}
              isSpotlightVariation={isSpotlightVariation}
              dataTestId="listing-card-message-mobile"
              origin={origin}
              size="md"
            />
          </Fragment>
        )}
        {!showMessageButton && !isShortTerm && (
          <Fragment>
            {paidPhoneHref && (
              <ListingCardCallButton
                entity={entity}
                onPaidPhoneClick={onPaidPhoneClick}
                origin={paidPhoneMixpanelOrigin ?? origin}
                paidPhoneHref={paidPhoneHref}
              />
            )}
            <button
              className={cn(css.seeDetailsBtn, buttonCss.solidButton, buttonCss.mdButton)}
              type="button"
            >
              See details
            </button>
          </Fragment>
        )}
        {isShortTerm && (
          <button
            onClick={onShortTermClick}
            className={cn(buttonCss.mdButton, buttonCss.solidButton, css.shortTermCta)}
            type="button"
          >
            {shouldOpenThirdParty && (
              <div className={css.externalLinkContainer}>
                <ExternalLink fill="#fff" />
              </div>
            )}
            <p className={css.shortTermCtaText}>
              {shouldOpenThirdParty ? 'Check availability' : 'See details'}
            </p>
          </button>
        )}
      </div>
    </Fragment>
  )
}
